import type {
  AuditRun,
  AuditRunStatus,
  BenefitRow,
  EnrollmentBenefits,
} from '../api/enrollment-overlap.types'

export const EMPTY_VALUE = '—'

/** How often a RUNNING run is re-fetched. */
export const POLL_INTERVAL_MS = 5000

/** A RUNNING run older than this is treated as abandoned by the backend. */
export const STALLED_AFTER_MS = 20 * 60 * 1000

export type DisplayStatus = AuditRunStatus | 'STALLED'

/** `null`/`undefined`/blank become the placeholder. */
export function orDash(value: string | null | undefined): string {
  return value === null || value === undefined || value.trim() === ''
    ? EMPTY_VALUE
    : value
}

/**
 * Counts are null unless the run SUCCEEDED. A null count must never look like
 * zero, so it renders as the placeholder.
 */
export function formatCount(count: number | null | undefined): string {
  return typeof count === 'number' && Number.isFinite(count)
    ? count.toLocaleString()
    : EMPTY_VALUE
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** ISO UTC timestamp in the user's locale and timezone. */
export function formatDateTime(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

const UTC_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
}

/**
 * A calendar date such as an enrollment start. These are midnight-UTC
 * timestamps, so they are formatted in UTC to avoid shifting a day in
 * timezones behind UTC.
 */
export function formatDate(value: string | null | undefined): string {
  const date = parseDate(value)
  if (!date) return EMPTY_VALUE
  return new Intl.DateTimeFormat('en-US', UTC_DATE_FORMAT).format(date)
}

/** Duration between start and completion; the placeholder while unfinished. */
export function formatDuration(
  startedAt: string | null | undefined,
  completedAt: string | null | undefined
): string {
  const start = parseDate(startedAt)
  const end = parseDate(completedAt)
  if (!start || !end) return EMPTY_VALUE
  const totalSeconds = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / 1000)
  )
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

function parseDateOnly(value: string): Date | null {
  const match = DATE_ONLY.exec(value)
  if (!match) return null
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  )
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * The range a run checked. The end is exclusive, so 2026-01-01 → 2027-01-01
 * is "2026"; anything else is "Mar 1, 2026 – Jun 30, 2026".
 */
export function formatDateScope(
  start: string | null | undefined,
  endExclusive: string | null | undefined
): string {
  const startDate = start ? parseDateOnly(start) : null
  const endDate = endExclusive ? parseDateOnly(endExclusive) : null
  if (!startDate || !endDate) return EMPTY_VALUE

  const isWholeYear =
    startDate.getUTCMonth() === 0 &&
    startDate.getUTCDate() === 1 &&
    endDate.getUTCMonth() === 0 &&
    endDate.getUTCDate() === 1 &&
    endDate.getUTCFullYear() === startDate.getUTCFullYear() + 1
  if (isWholeYear) return String(startDate.getUTCFullYear())

  const lastDay = new Date(endDate.getTime() - 24 * 60 * 60 * 1000)
  const format = new Intl.DateTimeFormat('en-US', UTC_DATE_FORMAT)
  return `${format.format(startDate)} – ${format.format(lastDay)}`
}

/** A RUNNING run that has been going on for more than ~20 minutes. */
export function isRunStalled(
  run: Pick<AuditRun, 'status' | 'startedAt'>,
  now: number = Date.now()
): boolean {
  if (run.status !== 'RUNNING') return false
  const started = parseDate(run.startedAt)
  return started !== null && now - started.getTime() > STALLED_AFTER_MS
}

export function getDisplayStatus(
  run: Pick<AuditRun, 'status' | 'startedAt'>,
  now: number = Date.now()
): DisplayStatus {
  return isRunStalled(run, now) ? 'STALLED' : run.status
}

/** A run that is genuinely in progress (RUNNING and not stalled). */
export function isRunActive(
  run: Pick<AuditRun, 'status' | 'startedAt'>,
  now: number = Date.now()
): boolean {
  return run.status === 'RUNNING' && !isRunStalled(run, now)
}

export function formatTrigger(
  run: Pick<AuditRun, 'triggerType' | 'requestedBy'>
): string {
  if (run.triggerType === 'SCHEDULED') return 'Scheduled'
  return run.requestedBy ? `Manual · ${run.requestedBy}` : 'Manual'
}

/**
 * Zips the parallel benefit arrays by index into one row per benefit. The
 * arrays are not guaranteed to be the same length and any element may be null,
 * so every column is read defensively.
 */
export function zipBenefits(
  benefits: EnrollmentBenefits | null | undefined
): BenefitRow[] {
  if (!benefits) return []
  const columns = [
    benefits.ids,
    benefits.types,
    benefits.names,
    benefits.carriers,
    benefits.active_statuses,
    benefits.application_statuses,
  ]
  const length = Math.max(
    0,
    ...columns.map((column) => (Array.isArray(column) ? column.length : 0))
  )
  return Array.from({ length }, (_, index) => ({
    id: benefits.ids?.[index] ?? null,
    type: benefits.types?.[index] ?? null,
    name: benefits.names?.[index] ?? null,
    carrier: benefits.carriers?.[index] ?? null,
    isActive: benefits.active_statuses?.[index] ?? null,
    applicationStatus: benefits.application_statuses?.[index] ?? null,
  }))
}

export function formatActive(isActive: boolean | null): string {
  if (isActive === null || isActive === undefined) return EMPTY_VALUE
  return isActive ? 'Active' : 'Inactive'
}

/** The name to show for an employee, falling back to the key when unknown. */
export function displayEmployeeName(employee: {
  fullName: string | null | undefined
  employeePublicKey: string
}): string {
  return employee.fullName?.trim() || employee.employeePublicKey
}

/** True when a non-empty page has no name on any row (likely a failed lookup). */
export function hasNoNames(
  employees: ReadonlyArray<{ fullName: string | null | undefined }>
): boolean {
  return (
    employees.length > 0 &&
    employees.every((employee) => !employee.fullName?.trim())
  )
}
